import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building, Shield, CheckCircle, AlertTriangle } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { useHasLicense } from "../contexts/LicenseContext";
import { useRBAC } from "../hooks/useRBAC";
import { useLiveDashboard } from '../hooks/useLiveDashboard';
import Navigation from "../components/Navigation";
import Card from "../components/Card";
import LoadingSpinner from "../components/LoadingSpinner";
import Button from "../components/Buttons";
import VendorCardsSection from "../components/VendorCardsSection";
import ReactSpeedometer from "react-d3-speedometer";
import { RiskScoreFootnote } from "./RiskScoreCards";
import IntelligenceNarrative from "../components/IntelligenceNarrative";
import { countActiveRisks } from "./shared/riskStatus";
import { severityPostureScore, severityPostureLevel } from '../utils/riskScoringEngine';
import { getComplianceBarColor, getComplianceTextColor, RISK_GAUGE_SEGMENT_STOPS, RISK_GAUGE_SEGMENT_COLORS } from '../utils/riskColors';

const TPRMDashboard = () => {
    const navigate = useNavigate();
    const apiCall = useApi();
    const hasLicense = useHasLicense();
    const showDeals = hasLicense('Deals');
    const { canViewOrganizationAssessments, canManageOrganizationRisks } = useRBAC();
    const [loading, setLoading] = useState(true);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [organizationAnalytics, setOrganizationAnalytics] = useState(null);
    const [predictiveRisk, setPredictiveRisk] = useState({ day45: 0, day90: 0, day180: 0 });
    const [loadingPredictive, setLoadingPredictive] = useState(true);
    const [riskVectors, setRiskVectors] = useState({});
    const [loadingRiskVectors, setLoadingRiskVectors] = useState(true);
    const [latestAssessment, setLatestAssessment] = useState(null);
    // null = VendorCardsSection hasn't reported yet; [] = loaded with no visible vendors
    // (e.g. a scoped user whose deals/BUs have no TPRM vendors — the headline cards must
    // still render, with zeroed numbers, instead of spinning forever).
    const [vendorRiskData, setVendorRiskData] = useState(null);

    // Raw (unscaled) values — scaled by remediation ratio in derived useEffect
    const [rawRiskVectors, setRawRiskVectors] = useState({});
    const [rawPredictive, setRawPredictive] = useState({ day45: 0, day90: 0, day180: 0 });

    // Redirect to tenant selection if no organization is selected
    useEffect(() => {
        const org = JSON.parse(localStorage.getItem("selectedOrganization"));
        if (!org?.organizationId) {
            navigate('/organizations');
        } else {
            setLoading(false);
        }
    }, [navigate]);

    // Load all dashboard source data. `silent` skips the loading-state toggles so the live background
    // refresh swaps numbers in place with no spinner/flicker; the mount call passes silent=false for
    // the normal first-load spinners. The independent fetches run in parallel.
    const refreshData = useCallback(async (silent = false) => {
        const org = JSON.parse(localStorage.getItem("selectedOrganization"));
        if (!org?.organizationId) return;

        const fetchPredictiveRisk = async () => {
            if (!silent) setLoadingPredictive(true);
            try {
                const data = await apiCall("GET", "/TPRMRisk/organization/predictive-risk");
                setRawPredictive({
                    day45: data?.predictive45DayScore || 0,
                    day90: data?.predictive90DayScore || 0,
                    day180: data?.predictive180DayScore || 0,
                });
            } catch (err) {
                setRawPredictive({ day45: 0, day90: 0, day180: 0 });
            } finally {
                if (!silent) setLoadingPredictive(false);
            }
        };

        const fetchRiskVectors = async () => {
            if (!silent) setLoadingRiskVectors(true);
            try {
                const data = await apiCall("GET", "/TPRMVendor/risk-vectors");
                setRawRiskVectors(data?.risk_status || {});
            } catch (err) {
                setRawRiskVectors({});
            } finally {
                if (!silent) setLoadingRiskVectors(false);
            }
        };

        await Promise.all([
            fetchPredictiveRisk(),
            fetchRiskVectors(),
        ]);
    }, [apiCall]);

    // First load (with spinners), then keep the dashboard live: the change-token poll re-runs the
    // fetch silently whenever risks/remediations change, with no visible loading state.
    useEffect(() => { refreshData(false); }, [refreshData]);
    const liveRefresh = useCallback(() => refreshData(true), [refreshData]);
    useLiveDashboard({ refresh: liveRefresh });

    // Derive analytics from vendor risk data (loaded by VendorCardsSection)
    useEffect(() => {
        if (vendorRiskData === null) return;

        const counts = {
            CRITICAL: vendorRiskData.reduce((s, v) => s + (v.criticalCount || 0), 0),
            HIGH: vendorRiskData.reduce((s, v) => s + (v.highCount || 0), 0),
            MEDIUM: vendorRiskData.reduce((s, v) => s + (v.mediumCount || 0), 0),
            LOW: vendorRiskData.reduce((s, v) => s + (v.lowCount || 0), 0),
        };
        const activeRisksCount = vendorRiskData.reduce((s, v) => s + (v.totalRisks || 0), 0);
        const remediatedCount = vendorRiskData.reduce((s, v) => s + (v.remediatedCount || 0), 0);
        const allRisksCount = activeRisksCount + remediatedCount;
        const score = severityPostureScore(counts);
        const level = severityPostureLevel(score);

        setOrganizationAnalytics({
            overall_risk_score: score,
            overall_RiskLevel: level,
            total_risks: allRisksCount,
            active_risks: activeRisksCount,
            risk_distribution: counts,
            remediated_risks: remediatedCount
        });

        setAnalytics({
            last_updated: new Date().toISOString()
        });

        setAnalyticsLoading(false);
    }, [vendorRiskData]);

    // Derived: pass through risk-table-based data (already excludes remediated risks)
    useEffect(() => {
        setRiskVectors(Object.fromEntries(
            Object.entries(rawRiskVectors).map(([k, v]) => [k, v || 0])
        ));
        setPredictiveRisk({
            day45: rawPredictive.day45,
            day90: rawPredictive.day90,
            day180: rawPredictive.day180,
        });
    }, [rawRiskVectors, rawPredictive]);

    if (loading) {
        return (
            <div className="min-h-screen bg-theme-primary">
                <Navigation />
                <div className="max-w-7xl mx-auto py-8 px-4">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-theme-primary">
            <Navigation />

            <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
                <div className="mb-3">
                    <h1 className="text-3xl font-bold text-content-primary">
                        <span className="text-green-600">TPRM Dashboard</span>
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Third-Party Risk Management Overview
                    </p>
                </div>

                {/* Intelligence Narrative — RISK_MANAGER+ at ORG only */}
                {canManageOrganizationRisks() && (
                    <IntelligenceNarrative
                        type="tprm"
                        title="Intelligence Narrative"
                        subtitle="AI-generated briefing based on third-party risks"
                        accentColor="emerald"
                    />
                )}

                {/* Top Bar - Risk Score Cards */}
                <div>
                    {analyticsLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                            <OverallRiskScoreCard analytics={organizationAnalytics} />
                            <RiskVectorCard riskVectors={riskVectors} loading={loadingRiskVectors} organizationAnalytics={organizationAnalytics} />
                            <VulnerabilityDistributionCard analytics={organizationAnalytics} />
                            <PredictiveRiskTimelineCard predictiveRisk={predictiveRisk} loading={loadingPredictive} analytics={organizationAnalytics} />
                        </div>
                    )}
                </div>

                {/* Vendor Cards */}
                <div className="mb-4">
                    <VendorCardsSection onVendorsLoaded={setVendorRiskData} />
                </div>

                {/* Vendor Assessments - Organization level */}
                {canViewOrganizationAssessments() && (
                    <div className="mb-4">
                        <VendorAssessmentsCard />
                    </div>
                )}

                {/* Business Units */}
                {showDeals && (
                    <div className="mb-4">
                        <RecentBUsCard />
                    </div>
                )}

                {/* Recent Deals with TPRM Assessments */}
                {showDeals && (
                    <div className="mb-4">
                        <TPRMDealsCard />
                    </div>
                )}

                {/* Remediation Status Tracker */}
                <div className="mb-4">
                    <RemediationStatusTracker />
                </div>

                {/* Recent Activity - hidden until dynamic data is implemented */}
                {/* <div className="mb-4">
                    <RecentActivityCard analytics={analytics} />
                </div> */}
            </div>
        </div>
    );
};

// Helper functions
const getRiskColor = (level) => {
    switch (level) {
        case "CRITICAL": return "text-red-700 bg-red-100";
        case "HIGH": return "text-orange-600 bg-orange-100";
        case "MEDIUM": return "text-yellow-600 bg-yellow-100";
        case "LOW": return "text-green-600 bg-green-100";
        default: return "text-gray-600 bg-gray-100";
    }
};

const getRiskBarColor = (riskLevel) => {
    switch (riskLevel) {
        case "CRITICAL": return "bg-red-700";
        case "HIGH": return "bg-orange-500";
        case "MEDIUM": return "bg-yellow-500";
        case "LOW": return "bg-green-500";
        default: return "bg-gray-400";
    }
};

const calculateRiskScore = (riskCounts = {}) => severityPostureScore(riskCounts);

const getRiskLevelFromScore = (score) => severityPostureLevel(score);

// Overall Risk Score Card with Speedometer
const OverallRiskScoreCard = ({ analytics }) => {
    const { overall_risk_score = 0, overall_RiskLevel = 'LOW', active_risks = 0, total_risks = 0 } = analytics || {};
    const hasAssessment = total_risks > 0;

    return (
        <Card className="text-center">
            <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Vendor Risk Score</h3>
            </div>

            <div className="w-full flex justify-center mb-2">
                <ReactSpeedometer
                    maxValue={100}
                    value={hasAssessment ? overall_risk_score : 0}
                    needleColor="#1f2937"
                    customSegmentStops={RISK_GAUGE_SEGMENT_STOPS}
                    segmentColors={RISK_GAUGE_SEGMENT_COLORS}
                    segments={RISK_GAUGE_SEGMENT_COLORS.length}
                    width={250}
                    height={150}
                    ringWidth={25}
                    needleTransitionDuration={1000}
                    needleTransition="easeElastic"
                    currentValueText={hasAssessment ? `${overall_risk_score}%` : " "}
                    needleHeightRatio={0.5}
                />
            </div>

            {hasAssessment ? (
                <>
                    <div className="flex justify-center space-x-2 mb-2">
                        <div className={`text-xs px-2 py-1 rounded ${getRiskColor(overall_RiskLevel)}`}>
                            {overall_RiskLevel}
                        </div>
                    </div>
                    <div className="text-xs text-gray-500">
                        {active_risks} Active Risks
                    </div>
                    <RiskScoreFootnote />
                </>
            ) : (
                <div className="text-xs text-gray-400 italic">Assessment Not Started</div>
            )}
        </Card>
    );
};

// Predictive Risk Timeline Card
const PredictiveRiskTimelineCard = ({ predictiveRisk, loading, analytics }) => {
    if (loading) {
        return (
            <Card className="text-center">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
                    Predictive Risk Timeline
                </h3>
                <div className="text-sm text-gray-500 py-6">Loading...</div>
            </Card>
        );
    }

    const { day45 = 0, day90 = 0, day180 = 0 } = predictiveRisk || {};
    const hasAssessment = (analytics?.total_risks || 0) > 0;

    return (
        <Card className="text-center">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
                Predictive Risk Timeline
            </h3>

            <div className="space-y-3">
                {/* 45 Days */}
                <div>
                    <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">45 Days</span>
                        <span className="font-medium text-yellow-700">{hasAssessment ? `${day45}%` : '—'}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: hasAssessment ? `${day45}%` : '0%' }}
                        />
                    </div>
                </div>

                {/* 90 Days */}
                <div>
                    <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">90 Days</span>
                        <span className="font-medium text-orange-700">{hasAssessment ? `${day90}%` : '—'}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: hasAssessment ? `${day90}%` : '0%' }}
                        />
                    </div>
                </div>

                {/* 180 Days */}
                <div>
                    <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">180 Days</span>
                        <span className="font-medium text-red-700">{hasAssessment ? `${day180}%` : '—'}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-red-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: hasAssessment ? `${day180}%` : '0%' }}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-4 text-xs text-gray-500">
                {hasAssessment ? 'Risk escalation forecast' : <span className="italic">Assessment Not Started</span>}
            </div>
        </Card>
    );
};

// Vulnerability Distribution Card
const VulnerabilityDistributionCard = ({ analytics }) => {
    const { risk_distribution = {}, total_risks = 0, active_risks = 0, remediated_risks = 0 } = analytics || {};
    const criticalCount = risk_distribution.CRITICAL || 0;
    const highCount = risk_distribution.HIGH || 0;
    const mediumCount = risk_distribution.MEDIUM || 0;
    const lowCount = risk_distribution.LOW || 0;
    const hasAssessment = total_risks > 0;

    return (
        <Card className="text-center">
            <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Vulnerability Distribution</h3>
            </div>

            {hasAssessment ? (
                <>
                    <div className="space-y-2 mb-4">
                        {criticalCount > 0 && (
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-red-700 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">Critical</span>
                                </div>
                                <span className="text-sm font-bold text-red-700">{criticalCount}</span>
                            </div>
                        )}
                        {highCount > 0 && (
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">High</span>
                                </div>
                                <span className="text-sm font-bold text-orange-600">{highCount}</span>
                            </div>
                        )}
                        {mediumCount > 0 && (
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">Medium</span>
                                </div>
                                <span className="text-sm font-bold text-yellow-600">{mediumCount}</span>
                            </div>
                        )}
                        {lowCount > 0 && (
                            <div className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">Low</span>
                                </div>
                                <span className="text-sm font-bold text-green-600">{lowCount}</span>
                            </div>
                        )}
                    </div>

                    {/* Risk counts summary */}
                    <div className="border-t pt-2 space-y-1">
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">Total Risks</span>
                            <span className="text-sm font-bold text-gray-800">{total_risks}</span>
                        </div>
                        {remediated_risks > 0 && (
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-green-600">Remediated</span>
                                <span className="text-sm font-bold text-green-600">{remediated_risks}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-blue-600">Active Risks</span>
                            <span className="text-sm font-bold text-blue-600">{active_risks}</span>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="space-y-2 mb-4">
                        {[
                            { label: 'Critical', dot: 'bg-red-200' },
                            { label: 'High', dot: 'bg-orange-200' },
                            { label: 'Medium', dot: 'bg-yellow-200' },
                            { label: 'Low', dot: 'bg-green-200' },
                        ].map(({ label, dot }) => (
                            <div key={label} className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className={`w-3 h-3 ${dot} rounded-full mr-2`}></div>
                                    <span className="text-sm font-medium text-gray-300">{label}</span>
                                </div>
                                <span className="text-sm font-bold text-gray-300">—</span>
                            </div>
                        ))}
                    </div>
                    <div className="border-t pt-2 text-xs text-gray-400 italic">Assessment Not Started</div>
                </>
            )}
        </Card>
    );
};

const AI_RISK_TYPE_LABELS = {
    TPE: "Third-Party Exposure",
    CM: "Configuration Management",
    BEC: "Business Email Compromise",
    AI: "AI Risk",
    OO: "Operational Outage",
    FF: "Financial Fraud",
    IP: "Intellectual Property Theft",
    SC: "Supply Chain",
    IT: "IT Infrastructure",
    DB: "Data Breach",
    RA: "Unauthorized Access",
    DD: "DDoS Attack",
    DDOS: "DDoS Attack",
    RW: "Ransomware",
};

// Risk Vector Card (matching DarkWeb ComplianceStatusCard pattern)
const RiskVectorCard = ({ riskVectors, loading, organizationAnalytics }) => {
    if (loading) {
        return (
            <Card className="text-center">
                <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Risk Vectors</h3>
                </div>
                <div className="text-sm text-gray-500 py-6">Loading...</div>
            </Card>
        );
    }

    const vectors = riskVectors || {};
    const vectorKeys = Object.keys(vectors);
    const [showAllVectors, setShowAllVectors] = useState(false);
    const VECTOR_LIMIT = 5;
    const hasAssessment = (organizationAnalytics?.total_risks || 0) > 0;

    if (!hasAssessment || vectorKeys.length === 0) {
        return (
            <Card className="text-center">
                <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Risk Vectors</h3>
                </div>
                <div className="text-xs text-gray-400 italic py-6">No risk data available</div>
            </Card>
        );
    }

    // Shared risk-vector color bands (see ../utils/riskColors).
    const getVectorColor = getComplianceTextColor;
    const getVectorBarColor = getComplianceBarColor;

    return (
        <Card className="text-center">
            <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Risk Vectors</h3>
            </div>

            <div className="space-y-2">
                {Object.entries(vectors).sort(([, a], [, b]) => b - a).slice(0, showAllVectors ? undefined : VECTOR_LIMIT).map(([vector, percentage]) => (
                    <div key={vector} className="text-xs">
                        <div className="flex justify-between mb-1">
                            <span className="text-gray-600">{AI_RISK_TYPE_LABELS[vector] || vector}</span>
                            <span className={`font-medium ${getVectorColor(percentage)}`}>
                                {percentage}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                                className={`${getVectorBarColor(percentage)} h-1.5 rounded-full`}
                                style={{ width: `${percentage}%` }}
                            />
                        </div>
                    </div>
                ))}
            </div>
            {vectorKeys.length > VECTOR_LIMIT && (
                <button
                    onClick={() => setShowAllVectors(prev => !prev)}
                    className="mt-3 w-full text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1 py-1 rounded hover:bg-indigo-50 transition-colors"
                >
                    {showAllVectors ? <>&#9650; Show less</> : <>&#9660; +{vectorKeys.length - VECTOR_LIMIT} more</>}
                </button>
            )}
        </Card>
    );
};

// Vendor Assessments Card - Shows org-level TPRM vendor assessments
const VendorAssessmentsCard = () => {
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);

    const apiCall = useApi();
    const navigate = useNavigate();

    const fetchVendorAssessments = useCallback(async (silent = false) => {
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) return;

            // Fetch org-level TPRM assessments (no dealId = org level)
            const data = await apiCall("GET", "/TPRMAssessments");
            const assessmentList = Array.isArray(data) ? data : [];

            // Fetch risks for each assessment to get counts
            const assessmentsWithRisks = await Promise.all(
                assessmentList.slice(0, 6).map(async (assessment) => {
                    try {
                        const risks = await apiCall("GET", `/TPRMAssessments/${assessment.tprmAssessmentID}/risks`);

                        if (!risks || !risks.length) {
                            return {
                                ...assessment,
                                riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
                                totalRisks: 0,
                                riskScore: 0,
                                hasRisks: false,
                            };
                        }

                        const normalize = (r) =>
                            (r?.userRating || r?.tprmUserRating || r?.residualRiskRating || r?.tprmResidualRiskRating || r?.inherentRiskRating || r?.tprmInherentRiskRating || "").toUpperCase();

                        const riskCounts = {
                            CRITICAL: risks.filter((r) => normalize(r) === "CRITICAL").length,
                            HIGH: risks.filter((r) => normalize(r) === "HIGH").length,
                            MEDIUM: risks.filter((r) => normalize(r) === "MEDIUM").length,
                            LOW: risks.filter((r) => normalize(r) === "LOW").length,
                        };

                        const riskScore = calculateRiskScore(riskCounts);

                        const activeRisks = countActiveRisks(risks);

                        return {
                            ...assessment,
                            riskCounts,
                            totalRisks: risks.length,
                            activeRisks,
                            riskScore,
                            hasRisks: true,
                        };
                    } catch {
                        return {
                            ...assessment,
                            riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
                            totalRisks: 0,
                            activeRisks: 0,
                            riskScore: 0,
                            hasRisks: false,
                        };
                    }
                })
            );

            setAssessments(assessmentsWithRisks);
        } catch (err) {
            setAssessments([]);
        } finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    useEffect(() => {
        fetchVendorAssessments(false);
    }, [fetchVendorAssessments]);

    const liveRefreshVendorAssessments = useCallback(() => fetchVendorAssessments(true), [fetchVendorAssessments]);
    useLiveDashboard({ refresh: liveRefreshVendorAssessments });

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Enterprise Assessments</h3>
                <button
                    onClick={() => navigate("/TPRMOrganizations")}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                    View All →
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : assessments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <Building className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No vendor assessments found</p>
                    <Button
                        variant="primary"
                        className="mt-4"
                        onClick={() => navigate("/TPRMOrganizations")}
                    >
                        Add Vendor Assessment
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {assessments.map((assessment) => {
                        const score = assessment.riskScore || 0;
                        const level = getRiskLevelFromScore(score);
                        const riskCounts = assessment.riskCounts || {};

                        return (
                            <div
                                key={assessment.tprmAssessmentID}
                                className="p-4 border border-gray-200 rounded-lg shadow hover:border-blue-300 transition-all duration-200"
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="font-semibold text-content-primary truncate">
                                        {assessment.vendorName || "Unknown Vendor"}
                                    </h4>

                                    {assessment.hasRisks ? (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            className="px-2 py-1 h-8 text-xs flex items-center"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/VendorDashboard/${assessment.vendorId}`);
                                            }}
                                        >
                                            <Shield className="h-4 w-4 mr-1" />
                                            View Risks ({assessment.activeRisks ?? assessment.totalRisks})
                                        </Button>
                                    ) : (
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            className="px-2 py-1 h-8 text-xs flex items-center"
                                            disabled
                                        >
                                            <Shield className="h-4 w-4 mr-1" />
                                            No Risks
                                        </Button>
                                    )}
                                </div>

                                <p className="text-xs text-gray-500">
                                    Status: <span className="capitalize">{assessment.tprmStatus || "pending"}</span>
                                </p>

                                <div className="mt-2">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs text-gray-600">Risk Level</span>
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${getRiskColor(level)}`}>
                                            {level}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div
                                            className={`h-1.5 rounded-full ${getRiskBarColor(level)}`}
                                            style={{ width: `${score}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Risk Distribution */}
                                {assessment.hasRisks && (
                                    <div className="mt-3 pt-2 border-t border-gray-100">
                                        <div className="grid grid-cols-4 gap-1 text-xs">
                                            {riskCounts.CRITICAL > 0 && (
                                                <div className="text-center">
                                                    <div className="font-bold text-red-600">{riskCounts.CRITICAL}</div>
                                                    <div className="text-gray-500">Critical</div>
                                                </div>
                                            )}
                                            {riskCounts.HIGH > 0 && (
                                                <div className="text-center">
                                                    <div className="font-bold text-orange-600">{riskCounts.HIGH}</div>
                                                    <div className="text-gray-500">High</div>
                                                </div>
                                            )}
                                            {riskCounts.MEDIUM > 0 && (
                                                <div className="text-center">
                                                    <div className="font-bold text-yellow-600">{riskCounts.MEDIUM}</div>
                                                    <div className="text-gray-500">Medium</div>
                                                </div>
                                            )}
                                            {riskCounts.LOW > 0 && (
                                                <div className="text-center">
                                                    <div className="font-bold text-green-600">{riskCounts.LOW}</div>
                                                    <div className="text-gray-500">Low</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
};

// Business Units Card
const RecentBUsCard = () => {
    const [businessUnits, setBusinessUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const apiCall = useApi();
    const navigate = useNavigate();

    const calcBURiskScore = (riskLevel) => {
        switch ((riskLevel || '').toUpperCase()) {
            case 'CRITICAL': return 95; case 'HIGH': return 80;
            case 'MEDIUM': return 60; case 'LOW': return 30; default: return 0;
        }
    };
    const getRiskLevelFromScore = (score) => severityPostureLevel(score);
    const getRiskColor = (level) => ({ CRITICAL: "bg-red-700", HIGH: "bg-orange-500", MEDIUM: "bg-yellow-500", LOW: "bg-green-500" }[level] || "bg-gray-400");

    const fetchBusinessUnits = useCallback(async (silent = false) => {
        try {
            const data = await apiCall("GET", "/businessUnits/summary?category=Vendor&pageSize=5");
            setBusinessUnits((data.items || []).map(bu => ({
                ...bu,
                riskCounts: { CRITICAL: bu.riskCounts?.critical || 0, HIGH: bu.riskCounts?.high || 0, MEDIUM: bu.riskCounts?.medium || 0, LOW: bu.riskCounts?.low || 0 },
                totalRisks: (bu.riskCounts?.critical || 0) + (bu.riskCounts?.high || 0) + (bu.riskCounts?.medium || 0) + (bu.riskCounts?.low || 0),
                riskScore: calcBURiskScore(bu.riskLevel),
                hasAssessment: (bu.applicationCount || 0) > 0,
            })));
        } catch {
        } finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    useEffect(() => {
        fetchBusinessUnits(false);
    }, [fetchBusinessUnits]);

    const liveRefreshBusinessUnits = useCallback(() => fetchBusinessUnits(true), [fetchBusinessUnits]);
    useLiveDashboard({ refresh: liveRefreshBusinessUnits });

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Business Units</h3>
                <button onClick={() => navigate("/TPRMBusinessUnits")} className="text-blue-600 hover:text-blue-800 text-sm font-medium">View All →</button>
            </div>
            {loading ? (
                <div className="flex items-center justify-center py-4"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
            ) : businessUnits.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm">No business unit assessments found.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {businessUnits.map((bu) => {
                        const score = bu.riskScore || 0; const level = getRiskLevelFromScore(score);
                        const hasRisk = bu.hasAssessment && bu.totalRisks > 0;
                        const badgeClass = level === 'CRITICAL' ? 'bg-red-100 text-red-700' : level === 'HIGH' ? 'bg-orange-100 text-orange-600' : level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600';
                        const scoreTextClass = level === 'CRITICAL' ? 'text-red-700' : level === 'HIGH' ? 'text-orange-600' : level === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600';
                        return (
                            <div key={bu.businessUnitId} className="relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300" onClick={() => navigate("/TPRMBusinessUnits")}>
                                <div className={`h-1 ${hasRisk ? getRiskColor(level) : 'bg-gray-200'}`} />
                                <div className="p-4">
                                    <div className="flex justify-between items-start gap-2 mb-1.5">
                                        <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{bu.businessUnitName}</h4>
                                        {hasRisk && <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{level}</span>}
                                    </div>
                                    {bu.domain && <p className="text-xs text-gray-400 mb-2">🌐 {bu.domain}</p>}
                                    {bu.assignedToUserName && (
                                        <div className="flex items-center gap-1.5 mb-2">
                                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0"><span className="text-[10px] font-bold text-gray-500">{bu.assignedToUserName[0]?.toUpperCase()}</span></div>
                                            <span className="text-xs text-gray-500 truncate">{bu.assignedToUserName}</span>
                                        </div>
                                    )}
                                    {hasRisk ? (
                                        <div className="mb-2">
                                            <div className="flex justify-between text-xs mb-1"><span className="text-gray-400">Risk Score</span><span className={`font-bold ${scoreTextClass}`}>{score}%</span></div>
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${getRiskColor(level)}`} style={{ width: `${score}%` }} /></div>
                                        </div>
                                    ) : <div className="text-xs text-gray-300 italic mb-2">No assessment yet</div>}
                                    {hasRisk && (
                                        <div className="flex justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                                            {bu.riskCounts.CRITICAL > 0 && <span className="text-red-600 font-medium">{bu.riskCounts.CRITICAL} Critical</span>}
                                            {bu.riskCounts.HIGH > 0 && <span className="text-orange-500 font-medium">{bu.riskCounts.HIGH} High</span>}
                                            {bu.riskCounts.MEDIUM > 0 && <span className="text-yellow-600 font-medium">{bu.riskCounts.MEDIUM} Med</span>}
                                            {bu.riskCounts.LOW > 0 && <span className="text-green-600 font-medium">{bu.riskCounts.LOW} Low</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
};

// TPRM Deals Card - Shows recent deals with TPRM assessments
const TPRMDealsCard = () => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDeal, setSelectedDeal] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [loadingVendors, setLoadingVendors] = useState(false);

    const apiCall = useApi();
    const navigate = useNavigate();

    const fetchDeals = useCallback(async (silent = false) => {
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) return;

            const data = await apiCall("GET", "/TPRMAssessments/recent-deals?limit=5");
            const dealListWithRisk = (Array.isArray(data) ? data : []).map(d => ({
                ...d,
                riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
                riskScore: 0,
                hasAssessment: false,
            }));
            setDeals(dealListWithRisk);
            dealListWithRisk.forEach(deal => fetchDealRisk(deal.dealID));
        } catch (err) {
            setDeals([]);
        } finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    useEffect(() => {
        fetchDeals(false);
    }, [fetchDeals]);

    const liveRefreshDeals = useCallback(() => fetchDeals(true), [fetchDeals]);
    useLiveDashboard({ refresh: liveRefreshDeals });

    const fetchDealRisk = async (dealID) => {
        try {
            const assessments = await apiCall("GET", `/TPRMAssessments?dealId=${dealID}`);
            const latestAssessment = assessments?.[0] || null;
            let risks = [];
            if (latestAssessment?.tprmAssessmentID) {
                risks = await apiCall("GET", `/TPRMAssessments/${latestAssessment.tprmAssessmentID}/risks`);
            }
            const normalize = (r) =>
                (r?.userRating || r?.tprmUserRating || r?.residualRiskRating || r?.tprmResidualRiskRating || r?.inherentRiskRating || r?.tprmInherentRiskRating || "").toUpperCase();
            const riskCounts = {
                CRITICAL: risks.filter(r => normalize(r) === "CRITICAL").length,
                HIGH: risks.filter(r => normalize(r) === "HIGH").length,
                MEDIUM: risks.filter(r => normalize(r) === "MEDIUM").length,
                LOW: risks.filter(r => normalize(r) === "LOW").length,
            };
            const riskScore = calculateRiskScore(riskCounts);
            setDeals(prev => prev.map(d =>
                d.dealID === dealID ? { ...d, riskCounts, riskScore, hasAssessment: !!latestAssessment } : d
            ));
        } catch (err) {}
    };

    const fetchVendors = async (dealID) => {
        setLoadingVendors(true);
        setVendors([]);

        try {
            // Fetch TPRM assessments for this deal
            const assessments = await apiCall("GET", `/TPRMAssessments?dealId=${dealID}`);
            const assessmentList = Array.isArray(assessments) ? assessments : [];

            // Fetch risks for each assessment
            const vendorsWithRisks = await Promise.all(
                assessmentList.map(async (assessment) => {
                    try {
                        const risks = await apiCall("GET", `/TPRMAssessments/${assessment.tprmAssessmentID}/risks`);

                        if (!risks || !risks.length) {
                            return {
                                ...assessment,
                                riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
                                totalRisks: 0,
                                riskScore: 0,
                                hasRisks: false,
                            };
                        }

                        const normalize = (r) =>
                            (r?.userRating || r?.tprmUserRating || r?.residualRiskRating || r?.tprmResidualRiskRating || r?.inherentRiskRating || r?.tprmInherentRiskRating || "").toUpperCase();

                        const riskCounts = {
                            CRITICAL: risks.filter((r) => normalize(r) === "CRITICAL").length,
                            HIGH: risks.filter((r) => normalize(r) === "HIGH").length,
                            MEDIUM: risks.filter((r) => normalize(r) === "MEDIUM").length,
                            LOW: risks.filter((r) => normalize(r) === "LOW").length,
                        };

                        const riskScore = calculateRiskScore(riskCounts);

                        return {
                            ...assessment,
                            riskCounts,
                            totalRisks: risks.length,
                            riskScore,
                            hasRisks: true,
                        };
                    } catch {
                        return {
                            ...assessment,
                            riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
                            totalRisks: 0,
                            riskScore: 0,
                            hasRisks: false,
                        };
                    }
                })
            );

            setVendors(vendorsWithRisks);
        } catch (err) {
            setVendors([]);
        } finally {
            setLoadingVendors(false);
        }
    };

    const handleDealClick = (deal) => {
        setSelectedDeal(deal);
        fetchVendors(deal.dealID);
    };

    const getRiskPercentage = (level) => {
        switch (level) {
            case "CRITICAL": return 90;
            case "HIGH": return 70;
            case "MEDIUM": return 50;
            case "LOW": return 25;
            default: return 0;
        }
    };

    if (!loading && deals.length === 0) return null;

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Recent Deals</h3>
                <button
                    onClick={() => navigate("/TPRMDeals")}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                    View All →
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                        {deals.map((deal) => {
                            const level = getRiskLevelFromScore(deal.riskScore || 0);
                            const hasRisk = deal.hasAssessment && Object.values(deal.riskCounts || {}).reduce((a, b) => a + b, 0) > 0;
                            const badgeClass = level === 'CRITICAL' ? 'bg-red-100 text-red-700' : level === 'HIGH' ? 'bg-orange-100 text-orange-600' : level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600';
                            const scoreTextClass = level === 'CRITICAL' ? 'text-red-700' : level === 'HIGH' ? 'text-orange-600' : level === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600';
                            return (
                            <div
                                key={deal.dealID}
                                className="relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300"
                                onClick={() => navigate("/TPRMDeals")}
                            >
                                <div className={`h-1 ${hasRisk ? getRiskBarColor(level) : 'bg-gray-200'}`} />
                                <div className="p-4">
                                    <div className="flex justify-between items-start gap-2 mb-1.5">
                                        <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{deal.dealName}</h4>
                                        {hasRisk && (
                                            <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{level}</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-400 mb-3">📍 {deal.dealCity}, {deal.dealCountry}</p>
                                    <div className="flex items-center gap-1.5 mb-3">
                                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                            <span className="text-[10px] font-bold text-gray-500">{(deal.dealOwner || 'N')[0]?.toUpperCase()}</span>
                                        </div>
                                        <span className="text-xs text-gray-500 truncate">{deal.dealOwner || 'N/A'}</span>
                                    </div>
                                    {hasRisk ? (
                                        <div className="mb-3">
                                            <div className="flex justify-between text-xs mb-1">
                                                <span className="text-gray-400">Risk Score</span>
                                                <span className={`font-bold ${scoreTextClass}`}>{deal.riskScore || 0}%</span>
                                            </div>
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div className={`h-full rounded-full ${getRiskBarColor(level)}`} style={{ width: `${getRiskPercentage(level)}%` }} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-xs text-gray-300 italic mb-3">No assessment yet</div>
                                    )}
                                    {(() => { const fmt = (d) => { const dt = d ? new Date(d) : null; return dt && !isNaN(dt) && dt.getFullYear() > 2000 ? dt.toLocaleDateString() : null; }; const s = fmt(deal.dealStartDate); const e = fmt(deal.dealEndDate); return (s || e) ? <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100"><span>{s || '—'}</span><span className="text-gray-300">→</span><span>{e || '—'}</span></div> : null; })()}
                                </div>
                            </div>
                            );
                        })}
                    </div>

                    {/* Vendors Section - shown when a deal is selected */}
                    {selectedDeal && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                            <h4 className="text-lg font-semibold mb-4">Vendors for {selectedDeal.dealName}</h4>
                            {loadingVendors ? (
                                <div className="flex items-center justify-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                                </div>
                            ) : vendors.length === 0 ? (
                                <p className="text-gray-500 py-4">No vendor assessments found for this deal.</p>
                            ) : (
                                <div className="space-y-3">
                                    {vendors.map((vendor) => {
                                        const score = vendor.riskScore || 0;
                                        const level = getRiskLevelFromScore(score);

                                        return (
                                            <div
                                                key={vendor.tprmAssessmentID}
                                                className="p-4 border border-gray-200 rounded-lg"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h5 className="font-medium text-content-primary">{vendor.vendorName || "Unknown Vendor"}</h5>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            Status: <span className="capitalize">{vendor.tprmStatus || "pending"}</span>
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-sm font-medium text-content-primary">Risk Score: {score}%</div>
                                                        <span className={`text-xs px-2 py-0.5 rounded ${getRiskColor(level)}`}>
                                                            {level}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="mt-3">
                                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full ${getRiskBarColor(level)}`}
                                                            style={{ width: `${score}%` }}
                                                        ></div>
                                                    </div>
                                                </div>

                                                {vendor.hasRisks && (
                                                    <div className="mt-3 flex space-x-4 text-xs">
                                                        {vendor.riskCounts.CRITICAL > 0 && (
                                                            <span className="text-red-600">
                                                                <strong>{vendor.riskCounts.CRITICAL}</strong> Critical
                                                            </span>
                                                        )}
                                                        {vendor.riskCounts.HIGH > 0 && (
                                                            <span className="text-orange-600">
                                                                <strong>{vendor.riskCounts.HIGH}</strong> High
                                                            </span>
                                                        )}
                                                        {vendor.riskCounts.MEDIUM > 0 && (
                                                            <span className="text-yellow-600">
                                                                <strong>{vendor.riskCounts.MEDIUM}</strong> Medium
                                                            </span>
                                                        )}
                                                        {vendor.riskCounts.LOW > 0 && (
                                                            <span className="text-green-600">
                                                                <strong>{vendor.riskCounts.LOW}</strong> Low
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </Card>
    );
};

// Remediation Status Tracker Component
const RemediationStatusTracker = () => {
    const apiCall = useApi();
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchRemediationSummary = useCallback(async (silent = false) => {
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) {
                return;
            }

            const response = await apiCall('GET', `/TPRMRemediations/summary`);
            setSummary(response);
        } catch (error) {
            setSummary({
                totalRemediations: 0,
                byStatus: [],
                byPriority: [],
                overdueCount: 0,
                notStartedCount: 0,
                inProgressCount: 0,
                openCount: 0,
                completedCount: 0,
                top_in_progress: []
            });
        } finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    useEffect(() => {
        fetchRemediationSummary(false);
    }, [fetchRemediationSummary]);

    const liveRefreshRemediationSummary = useCallback(() => fetchRemediationSummary(true), [fetchRemediationSummary]);
    useLiveDashboard({ refresh: liveRefreshRemediationSummary });

    if (loading) {
        return (
            <Card>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
            </Card>
        );
    }

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'CRITICAL': return 'text-red-700 bg-red-100';
            case 'HIGH': return 'text-orange-600 bg-orange-100';
            case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
            case 'LOW': return 'text-green-600 bg-green-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'text-green-600 bg-green-100';
            case 'In Progress': return 'text-blue-600 bg-blue-100';
            case 'Waiting': return 'text-yellow-600 bg-yellow-100';
            case 'Under Review': return 'text-purple-600 bg-purple-100';
            case 'Deferred': return 'text-gray-600 bg-gray-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <Card>
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-content-primary flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                    Remediation Status Tracker
                </h3>
                <button
                    onClick={() => navigate('/remediations?types=TPRM')}
                    className="text-sm text-blue-600 hover:text-blue-800"
                >
                    View All →
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{summary?.notStartedCount || 0}</div>
                    <div className="text-sm text-gray-500">Not Started</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{summary?.inProgressCount || 0}</div>
                    <div className="text-sm text-gray-500">In Progress</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{summary?.completedCount || 0}</div>
                    <div className="text-sm text-gray-500">Completed</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{summary?.overdueCount || 0}</div>
                    <div className="text-sm text-gray-500">Overdue</div>
                </div>
            </div>

            {/* SLA Metrics */}
            <div className="border-t border-gray-200 pt-4 mb-4">
                <h4 className="font-medium text-content-primary mb-3">SLA Performance</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <div className={`text-xl font-bold ${
                            (summary?.slaCompliancePercent ?? 100) >= 90 ? "text-green-600" :
                            (summary?.slaCompliancePercent ?? 100) >= 70 ? "text-yellow-600" : "text-red-600"
                        }`}>
                            {summary?.slaCompliancePercent ?? 100}%
                        </div>
                        <div className="text-xs text-gray-500">SLA Compliance</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <div className="text-xl font-bold text-red-600">{summary?.slaBreachCount || 0}</div>
                        <div className="text-xs text-gray-500">SLA Breaches</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <div className="text-xl font-bold text-gray-700">{summary?.avgDaysToResolve || 0}d</div>
                        <div className="text-xs text-gray-500">Avg Days to Resolve</div>
                    </div>
                </div>
                {summary?.agingBuckets && (summary.agingBuckets.days0To7 > 0 || summary.agingBuckets.days7To30 > 0 || summary.agingBuckets.days30To60 > 0 || summary.agingBuckets.days60Plus > 0) && (
                    <div>
                        <h4 className="text-sm font-medium text-gray-600 mb-2">Overdue Aging</h4>
                        <div className="flex gap-2">
                            {[
                                { label: "0-7d", count: summary.agingBuckets.days0To7, color: "bg-yellow-100 text-yellow-800" },
                                { label: "7-30d", count: summary.agingBuckets.days7To30, color: "bg-orange-100 text-orange-800" },
                                { label: "30-60d", count: summary.agingBuckets.days30To60, color: "bg-red-100 text-red-700" },
                                { label: "60+d", count: summary.agingBuckets.days60Plus, color: "bg-red-200 text-red-900" },
                            ].filter(b => b.count > 0).map(bucket => (
                                <span key={bucket.label} className={`px-3 py-1 rounded-full text-xs font-medium ${bucket.color}`}>
                                    {bucket.label}: {bucket.count}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {summary?.top_in_progress && summary.top_in_progress.length > 0 && (
                <div>
                    <h4 className="font-medium text-content-primary mb-3">Top In-Progress Tasks</h4>
                    <div className="space-y-2">
                        {summary.top_in_progress.slice(0, 5).map((remediation) => (
                            <div key={remediation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex-1">
                                    <div className="font-medium text-content-primary truncate">{remediation.title}</div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        🏢 {remediation.vendorName || 'Not Linked'}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate mt-1">
                                        ⚠️ Related to: {remediation.risk_name || 'Not Linked'}
                                    </div>
                                    <div className="flex items-center space-x-2 mt-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(remediation.priority)}`}>
                                            {remediation.priority}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(remediation.status)}`}>
                                            {remediation.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-medium text-content-primary">
                                        {remediation.progress_percentage || 0}%
                                    </div>
                                    <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
                                        <div
                                            className="bg-green-600 h-2 rounded-full"
                                            style={{ width: `${remediation.progress_percentage || 0}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
};

// Recent Activity Card
const RecentActivityCard = ({ analytics }) => {
    const activities = [
        {
            type: 'assessment_completion',
            title: 'New Vendor Assessment Completed',
            description: `${analytics?.recent_assessments || 0} vendor assessments active`,
            time: '2 hours ago',
            status: 'success'
        },
        {
            type: 'risk_update',
            title: 'Vendor Risk Rating Updated',
            description: 'Multiple vendor risks reassessed and updated',
            time: '1 day ago',
            status: 'warning'
        },
        {
            type: 'compliance_check',
            title: 'Vendor Compliance Check',
            description: 'Quarterly vendor compliance review completed',
            time: '2 days ago',
            status: 'info'
        }
    ];

    const getActivityIcon = (type) => {
        switch (type) {
            case 'assessment_completion': return CheckCircle;
            case 'risk_update': return AlertTriangle;
            case 'compliance_check': return Shield;
            default: return Shield;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'success': return 'text-green-600 bg-green-100';
            case 'warning': return 'text-yellow-600 bg-yellow-100';
            case 'info': return 'text-blue-600 bg-blue-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <Card>
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-4">
                {activities.map((activity, index) => {
                    const IconComponent = getActivityIcon(activity.type);
                    return (
                        <div key={index} className="flex items-start space-x-3">
                            <div className={`p-2 rounded-full ${getStatusColor(activity.status)}`}>
                                <IconComponent className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-content-primary">{activity.title}</p>
                                <p className="text-xs text-gray-500">{activity.description}</p>
                                <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

export default TPRMDashboard;
